import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import Badge from '../../components/Badge';

const PAID_STATUSES = ['paid', 'unlocked', 'checked_in'];

const CHECKIN_FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'checked_in', label: 'Eingecheckt' },
  { key: 'not_checked_in', label: 'Nicht eingecheckt' },
];

export default function AdminCheckin() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterCheckin, setFilterCheckin] = useState('all');
  const [search, setSearch] = useState('');
  const [tickets, setTickets] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const loadList = useCallback((eid) => {
    setListLoading(true);
    const params = new URLSearchParams();
    if (eid) params.set('event_id', eid);
    const qs = params.toString();
    api.get(qs ? `/api/admin/tickets?${qs}` : '/api/admin/tickets')
      .then((r) => setTickets(r.data.filter((t) => PAID_STATUSES.includes(t.status))))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    api.get('/api/events/all').then((r) => setEvents(r.data));
    loadList('');
  }, [loadList]);

  const checkin = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await api.post(`/api/checkin/${code.trim().toUpperCase()}`);
      setResult({ ...res.data, action: 'checkin' }); setCode(''); inputRef.current?.focus();
      loadList(filterEvent);
    } catch (err) { setError(err.response?.data?.detail || 'Ticket nicht gefunden'); }
    finally { setLoading(false); }
  };

  const checkinByCode = async (ticketCode) => {
    try {
      const res = await api.post(`/api/checkin/${ticketCode}`);
      setResult({ ...res.data, action: 'checkin' });
      loadList(filterEvent);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const checkout = async (ticketCode) => {
    try {
      const res = await api.post(`/api/checkout/${ticketCode}`);
      setResult({ ...res.data, action: 'checkout' });
      loadList(filterEvent);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const onEventChange = (e) => {
    const eid = e.target.value;
    setFilterEvent(eid);
    loadList(eid);
  };

  const visibleTickets = tickets.filter((t) => {
    if (filterCheckin === 'checked_in' && t.status !== 'checked_in') return false;
    if (filterCheckin === 'not_checked_in' && t.status === 'checked_in') return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const name = `${t.buyer?.firstname || ''} ${t.buyer?.lastname || ''}`.toLowerCase();
      const nickname = (t.buyer?.nickname || '').toLowerCase();
      const username = (t.buyer?.username || '').toLowerCase();
      if (!name.includes(q) && !nickname.includes(q) && !username.includes(q) && !t.ticket_code.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  const checkedInCount = tickets.filter((t) => t.status === 'checked_in').length;

  return (
    <div className="max-w-lg mx-auto">
      <h2 className="page-title">Check-in</h2>

      {/* Scanner */}
      <div className="card mb-4">
        <p className="text-sm text-slate-500 mb-3">Ticket-Code eingeben oder QR-Code scannen:</p>
        <form onSubmit={checkin} className="flex gap-2">
          <input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="Ticket-Code…" autoFocus autoCapitalize="characters" autoCorrect="off"
            className="input flex-1 font-mono font-bold tracking-widest uppercase text-base sm:text-lg min-w-0" />
          <Button type="submit" loading={loading} size="lg" className="flex-shrink-0">✓</Button>
        </form>
      </div>

      {error && (
        <div className="card border-l-4 border-l-red-400 bg-red-50 mb-4">
          <p className="text-red-700 font-semibold">❌ {error}</p>
        </div>
      )}

      {result && (
        <div className={`card border-l-4 mb-4 ${
          result.action === 'checkout' ? 'border-l-slate-400 bg-slate-50'
          : result.already_checked_in ? 'border-l-sand-400 bg-sand-50'
          : 'border-l-emerald-400 bg-emerald-50'
        }`}>
          <div className="flex justify-between items-start mb-4">
            <h3 className={`text-xl font-bold ${
              result.action === 'checkout' ? 'text-slate-600'
              : result.already_checked_in ? 'text-sand-700'
              : 'text-emerald-700'
            }`}>
              {result.action === 'checkout' ? '↩ Ausgecheckt' : result.already_checked_in ? '⚠️ Bereits eingecheckt!' : '✅ Eingecheckt!'}
            </h3>
            <Badge status={result.status} />
          </div>
          <dl className="space-y-1.5 text-sm">
            <div className="flex gap-3"><dt className="text-slate-500 w-24">Name</dt><dd className="font-semibold">{result.buyer_name}</dd></div>
            {result.nickname && <div className="flex gap-3"><dt className="text-slate-500 w-24">Nickname</dt><dd className="font-semibold">{result.nickname}</dd></div>}
            <div className="flex gap-3"><dt className="text-slate-500 w-24">Event</dt><dd className="font-semibold">{result.event_name}</dd></div>
            {result.seat_label && <div className="flex gap-3"><dt className="text-slate-500 w-24">Sitzplatz</dt><dd className="font-semibold">💺 {result.seat_label}</dd></div>}
            <div className="flex gap-3"><dt className="text-slate-500 w-24">Code</dt><dd className="font-mono text-slate-600">{result.ticket_code}</dd></div>
          </dl>
        </div>
      )}

      {/* Participant list */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-slate-500">Angemeldete &amp; bezahlte Teilnehmer:</p>
          <p className="text-xs font-semibold text-slate-500 whitespace-nowrap ml-2">
            {checkedInCount} / {tickets.length} eingecheckt
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <select className="input flex-1 min-w-40" value={filterEvent} onChange={onEventChange}>
            <option value="">Alle Events</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, Nickname oder Ticket-Code…"
            className="input flex-1 min-w-40"
          />
        </div>

        <div className="flex gap-1.5 mb-3 flex-wrap">
          {CHECKIN_FILTERS.map(({ key, label }) => {
            const count = key === 'all' ? tickets.length
              : key === 'checked_in' ? checkedInCount
              : tickets.length - checkedInCount;
            return (
              <button key={key} onClick={() => setFilterCheckin(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  filterCheckin === key
                    ? 'bg-ocean-600 text-white border-ocean-600'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-ocean-300 hover:text-ocean-600'
                }`}>
                {label} <span className={`ml-1 ${filterCheckin === key ? 'opacity-70' : 'opacity-50'}`}>({count})</span>
              </button>
            );
          })}
        </div>

        {listLoading && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-ocean-400 border-t-transparent" />
          </div>
        )}

        {!listLoading && visibleTickets.length > 0 && (
          <div className="space-y-2">
            {visibleTickets.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">
                    {t.buyer?.firstname} {t.buyer?.lastname}
                    {t.buyer?.nickname && <span className="text-slate-400 font-normal ml-1">· {t.buyer.nickname}</span>}
                  </p>
                  <p className="font-mono text-xs text-slate-500">{t.ticket_code}</p>
                  {t.seat_label && <p className="text-xs text-reef-600">💺 {t.seat_label}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge status={t.status} />
                  {t.status !== 'checked_in' && (
                    <Button variant="success" size="sm" onClick={() => checkinByCode(t.ticket_code)}>✓ Check-in</Button>
                  )}
                  {t.status === 'checked_in' && (
                    <Button variant="ghost" size="sm" onClick={() => checkout(t.ticket_code)}>↩ Auschecken</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!listLoading && visibleTickets.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-3">Keine Teilnehmer gefunden.</p>
        )}
      </div>
    </div>
  );
}
